/**
 * Personal Memory: typed, dated, cited facts about the owner's own life and work, kept in the Personal Brain table
 * (`MEM#<type>#<memId>`). Design and invariants: `apps/api/src/features/personalBrain/PERSONAL_BRAIN.md` § Memory M0.
 */

export enum PersonalMemoryType {
  DECISION = 'DECISION',
  COMMITMENT = 'COMMITMENT',
  PURCHASE = 'PURCHASE',
  SUBSCRIPTION = 'SUBSCRIPTION',
  BOOKING = 'BOOKING',
  DEADLINE = 'DEADLINE',
  MONEY_OWED = 'MONEY_OWED',
  PERSON_FACT = 'PERSON_FACT',
  PREFERENCE = 'PREFERENCE',
  PROJECT_STATE = 'PROJECT_STATE',
  DOCUMENT = 'DOCUMENT',
  NOTE = 'NOTE',
}

export enum PersonalMemoryState {
  /** Seen with low confidence: stored, never surfaced, expires on its own. */
  CANDIDATE = 'CANDIDATE',
  ACTIVE = 'ACTIVE',
  /** Surfaced, labelled unconfirmed, until the owner confirms it. */
  NEEDS_CONFIRMATION = 'NEEDS_CONFIRMATION',
  /** Learned without asking (learning mode AUTO): usable by the agent, labelled unconfirmed, until the owner confirms it. */
  DISCOVERED = 'DISCOVERED',
  SUPERSEDED = 'SUPERSEDED',
  /** The owner said it is wrong, or its last evidence is gone. Carries no content. */
  RETRACTED = 'RETRACTED',
}

export enum PersonalMemoryOrigin {
  /** The owner wrote it (a note, a confirmed `memory_remember` card). */
  DECLARED = 'DECLARED',
  /** Taken from a message the owner sent. */
  SELF = 'SELF',
  /** Derived by the assistant from several signals. */
  INFERRED = 'INFERRED',
  /** Taken from a message someone else wrote. */
  THIRD_PARTY = 'THIRD_PARTY',
}

export enum PersonalMemorySensitivity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  /** Special-category data (health, beliefs, orientation...): never on the card, never ACTIVE without the owner. */
  SPECIAL = 'SPECIAL',
}

export enum PersonalMemoryRetentionClass {
  /** A date that stops mattering soon after it passes. */
  EPHEMERAL = 'EPHEMERAL',
  /** A purchase, booking, document or debt: kept for the period it can still be disputed. */
  TRANSACTIONAL = 'TRANSACTIONAL',
  /** A relationship or state that is current until replaced. */
  ONGOING = 'ONGOING',
  /** A decision the owner may need to explain later. */
  DURABLE = 'DURABLE',
  /** A fact or preference that lives until superseded, retracted or forgotten. */
  PERSISTENT = 'PERSISTENT',
}

/** Days a memory outlives its anchor date (due, end or record date); null never expires by time. */
export const PERSONAL_MEMORY_RETENTION_DAYS: Record<
  PersonalMemoryRetentionClass,
  number | null
> = {
  [PersonalMemoryRetentionClass.EPHEMERAL]: 90,
  [PersonalMemoryRetentionClass.TRANSACTIONAL]: 730,
  [PersonalMemoryRetentionClass.ONGOING]: 400,
  [PersonalMemoryRetentionClass.DURABLE]: 1095,
  [PersonalMemoryRetentionClass.PERSISTENT]: null,
};

export const PERSONAL_MEMORY_RETENTION_CLASS_BY_TYPE: Record<
  PersonalMemoryType,
  PersonalMemoryRetentionClass
> = {
  [PersonalMemoryType.DECISION]: PersonalMemoryRetentionClass.DURABLE,
  [PersonalMemoryType.COMMITMENT]: PersonalMemoryRetentionClass.TRANSACTIONAL,
  [PersonalMemoryType.PURCHASE]: PersonalMemoryRetentionClass.TRANSACTIONAL,
  [PersonalMemoryType.SUBSCRIPTION]: PersonalMemoryRetentionClass.ONGOING,
  [PersonalMemoryType.BOOKING]: PersonalMemoryRetentionClass.TRANSACTIONAL,
  [PersonalMemoryType.DEADLINE]: PersonalMemoryRetentionClass.EPHEMERAL,
  [PersonalMemoryType.MONEY_OWED]: PersonalMemoryRetentionClass.TRANSACTIONAL,
  [PersonalMemoryType.PERSON_FACT]: PersonalMemoryRetentionClass.PERSISTENT,
  [PersonalMemoryType.PREFERENCE]: PersonalMemoryRetentionClass.PERSISTENT,
  [PersonalMemoryType.PROJECT_STATE]: PersonalMemoryRetentionClass.ONGOING,
  [PersonalMemoryType.DOCUMENT]: PersonalMemoryRetentionClass.TRANSACTIONAL,
  [PersonalMemoryType.NOTE]: PersonalMemoryRetentionClass.PERSISTENT,
};

/** What the owner may write by hand: a free note or a preference. Everything else comes from evidence. */
export const PERSONAL_MEMORY_DECLARABLE_TYPES: readonly PersonalMemoryType[] = [
  PersonalMemoryType.NOTE,
  PersonalMemoryType.PREFERENCE,
];

/** Types that assert what the owner decided, wants or promised: another person's message cannot make one ACTIVE. */
export const PERSONAL_MEMORY_OWNER_ASSERTED_TYPES: readonly PersonalMemoryType[] =
  [
    PersonalMemoryType.DECISION,
    PersonalMemoryType.PREFERENCE,
    PersonalMemoryType.COMMITMENT,
  ];

export const PERSONAL_MEMORY_SUMMARY_MAX_CHARS = 400;
export const PERSONAL_MEMORY_QUOTE_MAX_CHARS = 240;
export const PERSONAL_MEMORY_NOTE_MAX_CHARS = 500;
export const PERSONAL_MEMORY_FIELDS_MAX_CHARS = 2000;
export const PERSONAL_MEMORY_MAX_EVIDENCE = 10;
export const PERSONAL_MEMORY_MAX_ENTITIES = 5;

export enum PersonalMemoryPurchaseStatus {
  ORDERED = 'ORDERED',
  SHIPPED = 'SHIPPED',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
}

export enum PersonalMemorySubscriptionCycle {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  WEEKLY = 'WEEKLY',
  OTHER = 'OTHER',
}

export enum PersonalMemorySubscriptionStatus {
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
}

export enum PersonalMemoryBookingKind {
  FLIGHT = 'FLIGHT',
  LODGING = 'LODGING',
  EVENT = 'EVENT',
  RENTAL_CAR = 'RENTAL_CAR',
  RESTAURANT = 'RESTAURANT',
  TRAIN = 'TRAIN',
  BUS = 'BUS',
  OTHER = 'OTHER',
}

export enum PersonalMemoryBookingStatus {
  CONFIRMED = 'CONFIRMED',
  CANCELLED = 'CANCELLED',
}

export enum PersonalMemoryMoneyDirection {
  I_OWE = 'I_OWE',
  OWED_TO_ME = 'OWED_TO_ME',
}

export interface PersonalMemoryStatusChange<TStatus extends string> {
  status: TStatus;
  /** ISO-8601 of the message that reported it. */
  at: string;
}

/** Past statuses of a memory updated in place, oldest first, capped at PERSONAL_MEMORY_STATUS_HISTORY_MAX. */
export const PERSONAL_MEMORY_STATUS_HISTORY_MAX = 6;

export interface PersonalMemoryPurchaseItem {
  name: string;
  quantity?: number;
  price?: number;
}

/**
 * `fields` of a PURCHASE memory. `amount` and `currency` are absent when the only message seen is a shipping or
 * delivery notice, which carries neither.
 */
export interface PersonalMemoryPurchaseFields {
  merchant: string;
  orderNumber: string;
  items?: PersonalMemoryPurchaseItem[];
  amount?: number;
  currency?: string;
  status: PersonalMemoryPurchaseStatus;
  trackingNumber?: string;
  carrier?: string;
  statusHistory?: PersonalMemoryStatusChange<PersonalMemoryPurchaseStatus>[];
}

/** `fields` of a SUBSCRIPTION memory. `renewalDate` is ISO-8601 and is also the row's `dueAt`; `noticeDays` is also the row's `noticeDays`. */
export interface PersonalMemorySubscriptionFields {
  counterparty: string;
  plan?: string;
  amount?: number;
  currency?: string;
  cycle: PersonalMemorySubscriptionCycle;
  renewalDate?: string;
  noticeDays?: number;
  status: PersonalMemorySubscriptionStatus;
  statusHistory?: PersonalMemoryStatusChange<PersonalMemorySubscriptionStatus>[];
}

/** `fields` of a BOOKING memory. `startAt` is ISO-8601 and is also the row's `validFrom` and `dueAt`; `endAt` is the row's `validTo`. */
export interface PersonalMemoryBookingFields {
  kind: PersonalMemoryBookingKind;
  provider: string;
  reservationNumber?: string;
  startAt: string;
  endAt?: string;
  /** Sealed with the rest of the payload; sensitivity HIGH on the row. */
  location?: string;
  status: PersonalMemoryBookingStatus;
  statusHistory?: PersonalMemoryStatusChange<PersonalMemoryBookingStatus>[];
}

/** `fields` of a MONEY_OWED memory. `direction` I_OWE sets the row's `owedByOwner`; `dueAt` is also the row's `dueAt`; `paidAt` takes it off the due index. */
export interface PersonalMemoryMoneyOwedFields {
  direction: PersonalMemoryMoneyDirection;
  counterparty: string;
  amount: number;
  currency: string;
  dueAt?: string;
  invoiceNumber?: string;
  paidAt?: string;
}

export enum PersonalMemoryCommitmentDirection {
  /** The owner promised it, in a message the owner sent. */
  I_OWE = 'I_OWE',
  /** The counterparty promised it, in a message the counterparty sent. */
  OWED_TO_ME = 'OWED_TO_ME',
}

export enum PersonalMemoryCommitmentStatus {
  OPEN = 'OPEN',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED',
  /** Derived at read time from `dueAt` while OPEN; the daily alert job never re-seals a row to store it. */
  OVERDUE = 'OVERDUE',
}

/**
 * `fields` of a COMMITMENT memory. `direction` I_OWE sets the row's `owedByOwner`; `dueAt` (ISO-8601, end of the day for a
 * bare date) is also the row's `dueAt` while the commitment is OPEN. `dueText` is the original phrase ("by Friday").
 * `closedBy` is the stable-id digest of the message that closed it.
 */
export interface PersonalMemoryCommitmentFields {
  direction: PersonalMemoryCommitmentDirection;
  counterparty: string;
  action: string;
  dueAt?: string;
  dueText?: string;
  status: PersonalMemoryCommitmentStatus;
  statusHistory?: PersonalMemoryStatusChange<PersonalMemoryCommitmentStatus>[];
  closedBy?: string;
}

export type PersonalMemoryTransactionalFields =
  | PersonalMemoryPurchaseFields
  | PersonalMemorySubscriptionFields
  | PersonalMemoryBookingFields
  | PersonalMemoryMoneyOwedFields;

/** The types the transactional extractor writes. */
export const PERSONAL_MEMORY_TRANSACTIONAL_TYPES: readonly PersonalMemoryType[] =
  [
    PersonalMemoryType.PURCHASE,
    PersonalMemoryType.SUBSCRIPTION,
    PersonalMemoryType.BOOKING,
    PersonalMemoryType.MONEY_OWED,
  ];

export enum PersonalMemoryDecisionMaker {
  OWNER = 'OWNER',
  COUNTERPART = 'COUNTERPART',
  GROUP = 'GROUP',
}

/**
 * `fields` of a DECISION memory. `decidedAt` (ISO-8601) is also the row's `validFrom`, so "what had I decided in March"
 * reads the validity window; a later decision on the same topic closes `validTo`. `decidedBy` is what the message says;
 * the row's origin comes from who wrote it (a decision only the owner can make is ACTIVE only from an owner message).
 */
export interface PersonalMemoryDecisionFields {
  topic: string;
  choice: string;
  alternatives?: string[];
  decidedBy: PersonalMemoryDecisionMaker;
  rationale?: string;
  decidedAt: string;
}

export enum PersonalMemoryDeadlineOwner {
  /** The owner has to deliver or do it. */
  OWNER = 'OWNER',
  COUNTERPART = 'COUNTERPART',
}

export enum PersonalMemoryDeadlineStatus {
  OPEN = 'OPEN',
  DONE = 'DONE',
  MISSED = 'MISSED',
  /** A later message gave another date; the new date is a separate memory this one points to. */
  MOVED = 'MOVED',
}

/**
 * `fields` of a DEADLINE memory. `dueAt` (ISO-8601, end of the day for a bare date) is also the row's `dueAt` while the
 * status is OPEN; `owner` OWNER sets the row's `owedByOwner`. `dueText` is the original phrase, verbatim.
 */
export interface PersonalMemoryDeadlineFields {
  what: string;
  dueAt: string;
  dueText?: string;
  owner: PersonalMemoryDeadlineOwner;
  status: PersonalMemoryDeadlineStatus;
  statusHistory?: PersonalMemoryStatusChange<PersonalMemoryDeadlineStatus>[];
}

export enum PersonalMemoryProjectStatus {
  ON_TRACK = 'ON_TRACK',
  AT_RISK = 'AT_RISK',
  BLOCKED = 'BLOCKED',
  DONE = 'DONE',
  UNKNOWN = 'UNKNOWN',
}

/**
 * `fields` of a PROJECT_STATE memory: a snapshot as of `asOf` (ISO-8601, also the row's `validFrom`). A later snapshot of the
 * same project supersedes it, so the history of the project is the chain of closed snapshots.
 */
export interface PersonalMemoryProjectStateFields {
  project: string;
  status: PersonalMemoryProjectStatus;
  blockers?: string[];
  nextStep?: string;
  asOf: string;
}

/** The types the conversational fact extractor writes (decisions, deadlines, project state). */
export const PERSONAL_MEMORY_CONVERSATIONAL_FACT_TYPES: readonly PersonalMemoryType[] =
  [
    PersonalMemoryType.DECISION,
    PersonalMemoryType.DEADLINE,
    PersonalMemoryType.PROJECT_STATE,
  ];

export enum PersonalMemoryPersonFactPredicate {
  BIRTHDAY = 'BIRTHDAY',
  FAMILY = 'FAMILY',
  ROLE_CHANGE = 'ROLE_CHANGE',
  COMPANY_CHANGE = 'COMPANY_CHANGE',
  LOCATION = 'LOCATION',
  INTEREST = 'INTEREST',
  HEALTH = 'HEALTH',
  OTHER = 'OTHER',
}

/**
 * `fields` of a PERSON_FACT memory: one fact about a counterpart, sealed with the rest of the payload. The person is the
 * row's primary entity (their counterpart key), so forgetting the person deletes the fact. `asOf` (ISO-8601) is also the
 * row's `validFrom`. Third-party personal data: sensitivity is at least HIGH, SPECIAL for health and the other
 * special categories, and the state starts NEEDS_CONFIRMATION whatever the origin.
 */
export interface PersonalMemoryPersonFactFields {
  /** How the message names the person; the key that identifies them is the row's entity, never this text. */
  subjectLabel: string;
  predicate: PersonalMemoryPersonFactPredicate;
  value: string;
  asOf?: string;
}

/** A role, company or place is single-valued: a newer fact about the same person and predicate replaces the older. */
export const PERSONAL_MEMORY_SUPERSEDING_PERSON_PREDICATES: readonly PersonalMemoryPersonFactPredicate[] =
  [
    PersonalMemoryPersonFactPredicate.ROLE_CHANGE,
    PersonalMemoryPersonFactPredicate.COMPANY_CHANGE,
    PersonalMemoryPersonFactPredicate.LOCATION,
  ];

export enum PersonalMemoryPreferenceScope {
  SCHEDULING = 'SCHEDULING',
  COMMUNICATION = 'COMMUNICATION',
  TRAVEL = 'TRAVEL',
  FOOD = 'FOOD',
  TOOLS = 'TOOLS',
  WORK_STYLE = 'WORK_STYLE',
  OTHER = 'OTHER',
}

/**
 * `fields` of a PREFERENCE memory taken from a message the owner wrote: how the owner likes things done. `statement` is the
 * owner's own words, shortened to one line. `appliesTo` is the label of the one counterpart the preference is about
 * ("with Ana"); the counterpart's key is then also an entity of the row.
 */
export interface PersonalMemoryPreferenceFields {
  scope: PersonalMemoryPreferenceScope;
  statement: string;
  appliesTo?: string;
}

/** Types the extractor can never leave ACTIVE: the owner confirms the first time, whoever wrote the message. */
export const PERSONAL_MEMORY_CONFIRM_FIRST_TYPES: readonly PersonalMemoryType[] =
  [PersonalMemoryType.PERSON_FACT, PersonalMemoryType.PREFERENCE];

/** Types about a third party's personal data: the stored sensitivity is never lower than HIGH. */
export const PERSONAL_MEMORY_HIGH_SENSITIVITY_TYPES: readonly PersonalMemoryType[] =
  [PersonalMemoryType.PERSON_FACT, PersonalMemoryType.DOCUMENT];

/** Person facts are replaced over time: "what was true then" reads their validity window like the other fact types. */
export const PERSONAL_MEMORY_VALIDITY_WINDOW_TYPES: readonly PersonalMemoryType[] =
  [
    ...PERSONAL_MEMORY_CONVERSATIONAL_FACT_TYPES,
    PersonalMemoryType.PERSON_FACT,
  ];

export enum PersonalMemoryDocumentKind {
  CONTRACT = 'CONTRACT',
  INVOICE = 'INVOICE',
  RECEIPT = 'RECEIPT',
  PROPOSAL = 'PROPOSAL',
  STATEMENT = 'STATEMENT',
  OTHER = 'OTHER',
}

/** Kinds that bind the owner to money or terms: the owner confirms them before they alert or surface as fact. */
export const PERSONAL_MEMORY_CONFIRM_FIRST_DOCUMENT_KINDS: readonly PersonalMemoryDocumentKind[] =
  [PersonalMemoryDocumentKind.CONTRACT, PersonalMemoryDocumentKind.INVOICE];

/**
 * `fields` of a DOCUMENT memory: what a contract, invoice, receipt, proposal or statement attachment says, taken from the
 * text already extracted from it (no OCR). Dates are ISO-8601. `effectiveDate` / `endDate` are the row's `validFrom` /
 * `validTo`. `dueDate` is the payment due date of an invoice. `attachmentId` and `fileName` come from the attachment row,
 * never from the model. The row is sealed with the rest of the payload and its sensitivity is at least HIGH.
 */
export interface PersonalMemoryDocumentFields {
  kind: PersonalMemoryDocumentKind;
  title: string;
  parties?: string[];
  effectiveDate?: string;
  endDate?: string;
  renewalDate?: string;
  noticeDays?: number;
  dueDate?: string;
  amount?: number;
  currency?: string;
  attachmentId: string;
  fileName?: string;
}
